import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { IconButton } from '../../components/brand/BrandButton';
import { Heading, SmallText, BodyText } from '../../components/brand/BrandTypography';
import { Container } from '../../components/brand/BrandLayout';

// Constants
import { API_URL, TAB_LIST } from './constants';

// Modal Components
import ToolEditorModal from './modals/ToolEditorModal';

// Tab Components
import PagesTab from './tabs/PagesTab';
import BlogTab from './tabs/BlogTab';
import TestimonialsTab from './tabs/TestimonialsTab';
import ToolsTab from './tabs/ToolsTab';
import MeditationsTab from './tabs/MeditationsTab';
import VoiceSettingsTab from './tabs/VoiceSettingsTab';
import PathsTab from './tabs/PathsTab';
import AssessmentTab from './tabs/AssessmentTab';
import EmotionsTab from './tabs/EmotionsTab';
import EducationTab from './tabs/EducationTab';
import ScriptsTab from './tabs/ScriptsTab';
import TriggersTab from './tabs/TriggersTab';
import PatternsTab from './tabs/PatternsTab';
import CheckinTab from './tabs/CheckinTab';
import SubscribersTab from './tabs/SubscribersTab';
import SupportTab from './tabs/SupportTab';
import RepairTab from './tabs/RepairTab';
import DisclaimerTab from './tabs/DisclaimerTab';
import SubscriptionTiersTab from './tabs/SubscriptionTiersTab';

const ToolEditor = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Core state
    const [tools, setTools] = useState([]);
    const [paths, setPaths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pages');
    const [filterType, setFilterType] = useState('all');
    const [isAdmin, setIsAdmin] = useState(null);
    
    // Tool editing state
    const [editingTool, setEditingTool] = useState(null);
    const [isNewTool, setIsNewTool] = useState(false);
    const [previewTool, setPreviewTool] = useState(null);
    
    // Page content data
    const [pageContents, setPageContents] = useState([]);
    
    // Relationship data
    const [emotions, setEmotions] = useState([]);
    const [scripts, setScripts] = useState([]);
    const [triggers, setTriggers] = useState([]);
    const [cyclePatterns, setCyclePatterns] = useState([]);
    const [checkinQuestions, setCheckinQuestions] = useState([]);
    const [safetyQuestions, setSafetyQuestions] = useState([]);
    
    // Assessment config
    const [assessmentConfig, setAssessmentConfig] = useState(null);
    const [editingAssessmentSection, setEditingAssessmentSection] = useState(null);
    
    // Subscribers data
    const [subscribersData, setSubscribersData] = useState({ subscribers: [], stats: {} });
    const [subscribersLoading, setSubscribersLoading] = useState(false);
    
    // Support tickets data
    const [supportTickets, setSupportTickets] = useState({ tickets: [], stats: {} });
    const [supportLoading, setSupportLoading] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [contactSubmissions, setContactSubmissions] = useState([]);
    
    // Repair & Reconnection content
    const [repairContent, setRepairContent] = useState(null);
    const [repairLoading, setRepairLoading] = useState(false);
    const [editingRepairSection, setEditingRepairSection] = useState(null);
    
    // Edit states for relationship data
    const [editingEmotion, setEditingEmotion] = useState(null);
    const [editingScript, setEditingScript] = useState(null);
    const [editingTrigger, setEditingTrigger] = useState(null);
    const [editingPattern, setEditingPattern] = useState(null);
    const [editingCheckin, setEditingCheckin] = useState(null);

    // Read tab from URL query params
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam && TAB_LIST.some(t => t.id === tabParam)) {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    // Check admin access
    useEffect(() => {
        const checkAdmin = async () => {
            try {
                const token = localStorage.getItem('co_regulate_token');
                if (!token) {
                    setIsAdmin(false);
                    return;
                }
                const response = await fetch(`${API_URL}/api/auth/is-admin`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setIsAdmin(data.is_admin);
                } else {
                    setIsAdmin(false);
                }
            } catch (err) {
                setIsAdmin(false);
            }
        };
        checkAdmin();
    }, []);

    // Fetch data
    const fetchData = async () => {
        setLoading(true);
        try {
            const [toolsRes, pathsRes, pagesRes, emotionsRes, scriptsRes, triggersRes, patternsRes, checkinRes, safetyRes, assessmentRes] = await Promise.all([
                fetch(`${API_URL}/api/tools?active_only=false`),
                fetch(`${API_URL}/api/tools/paths/all?active_only=false`),
                fetch(`${API_URL}/api/pages/`),
                fetch(`${API_URL}/api/relationship/emotions`),
                fetch(`${API_URL}/api/relationship/scripts`),
                fetch(`${API_URL}/api/relationship/adhd-triggers`),
                fetch(`${API_URL}/api/relationship/cycle-patterns`),
                fetch(`${API_URL}/api/relationship/checkin-questions`),
                fetch(`${API_URL}/api/relationship/safety-questions`),
                fetch(`${API_URL}/api/assessment/config`)
            ]);
            
            if (toolsRes.ok) setTools(await toolsRes.json());
            if (pathsRes.ok) setPaths(await pathsRes.json());
            if (pagesRes.ok) {
                const pages = await pagesRes.json();
                if (pages.length === 0) {
                    const impactRes = await fetch(`${API_URL}/api/pages/impact`);
                    if (impactRes.ok) {
                        setPageContents([await impactRes.json()]);
                    }
                } else {
                    setPageContents(pages);
                }
            }
            if (emotionsRes.ok) setEmotions(await emotionsRes.json());
            if (scriptsRes.ok) setScripts(await scriptsRes.json());
            if (triggersRes.ok) setTriggers(await triggersRes.json());
            if (patternsRes.ok) setCyclePatterns(await patternsRes.json());
            if (checkinRes.ok) setCheckinQuestions(await checkinRes.json());
            if (safetyRes.ok) setSafetyQuestions(await safetyRes.json());
            if (assessmentRes.ok) setAssessmentConfig(await assessmentRes.json());
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load tools');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Tool handlers
    const handleSaveTool = async (toolData, isNew) => {
        try {
            const url = isNew 
                ? `${API_URL}/api/tools` 
                : `${API_URL}/api/tools/${toolData.tool_id}`;
            
            const response = await fetch(url, {
                method: isNew ? 'POST' : 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(toolData)
            });

            if (response.ok) {
                toast.success(isNew ? 'Tool created!' : 'Tool updated!');
                fetchData();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to save tool');
            }
        } catch (error) {
            toast.error('Failed to save tool');
            throw error;
        }
    };

    const handleDeleteTool = async (tool) => {
        if (!window.confirm(`Are you sure you want to delete "${tool.name}"?`)) return;
        
        try {
            const response = await fetch(`${API_URL}/api/tools/${tool.tool_id}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                toast.success('Tool deactivated');
                fetchData();
            }
        } catch (error) {
            toast.error('Failed to delete tool');
        }
    };

    // Save handlers for relationship data
    const saveEmotion = async (emotion) => {
        try {
            const response = await fetch(`${API_URL}/api/relationship/emotions/${emotion.emotion_id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                },
                body: JSON.stringify(emotion)
            });
            if (response.ok) {
                toast.success('Emotion updated');
                setEditingEmotion(null);
                fetchData();
            } else {
                toast.error('Failed to update emotion');
            }
        } catch (error) {
            toast.error('Failed to save emotion');
        }
    };

    const saveScript = async (script) => {
        try {
            const response = await fetch(`${API_URL}/api/relationship/scripts/${script.script_id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                },
                body: JSON.stringify(script)
            });
            if (response.ok) {
                toast.success('Script updated');
                setEditingScript(null);
                fetchData();
            } else {
                toast.error('Failed to update script');
            }
        } catch (error) {
            toast.error('Failed to save script');
        }
    };

    const saveTrigger = async (trigger) => {
        try {
            const response = await fetch(`${API_URL}/api/relationship/adhd-triggers/${trigger.trigger_id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                },
                body: JSON.stringify(trigger)
            });
            if (response.ok) {
                toast.success('Trigger updated');
                setEditingTrigger(null);
                fetchData();
            } else {
                toast.error('Failed to update trigger');
            }
        } catch (error) {
            toast.error('Failed to save trigger');
        }
    };

    const savePattern = async (pattern) => {
        try {
            const response = await fetch(`${API_URL}/api/relationship/cycle-patterns/${pattern.pattern_id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                },
                body: JSON.stringify(pattern)
            });
            if (response.ok) {
                toast.success('Pattern updated');
                setEditingPattern(null);
                fetchData();
            } else {
                toast.error('Failed to update pattern');
            }
        } catch (error) {
            toast.error('Failed to save pattern');
        }
    };

    const saveCheckin = async (question) => {
        try {
            const response = await fetch(`${API_URL}/api/relationship/checkin-questions/${question.question_id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                },
                body: JSON.stringify(question)
            });
            if (response.ok) {
                toast.success('Question updated');
                setEditingCheckin(null);
                fetchData();
            } else {
                toast.error('Failed to update question');
            }
        } catch (error) {
            toast.error('Failed to save question');
        }
    };

    const saveAssessmentConfig = async (config) => {
        try {
            const response = await fetch(`${API_URL}/api/assessment/config`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                },
                body: JSON.stringify(config)
            });
            if (response.ok) {
                toast.success('Assessment config updated');
                setEditingAssessmentSection(null);
                fetchData();
            } else {
                toast.error('Failed to update assessment');
            }
        } catch (error) {
            toast.error('Failed to save assessment config');
        }
    };

    // Subscriber handlers
    const fetchSubscribers = async () => {
        setSubscribersLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/admin/subscribers`, {
                headers: { 
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setSubscribersData(data);
            } else {
                toast.error('Failed to load subscribers');
            }
        } catch (error) {
            toast.error('Failed to fetch subscribers');
        } finally {
            setSubscribersLoading(false);
        }
    };

    // Support handlers
    const fetchSupportTickets = async () => {
        setSupportLoading(true);
        try {
            const [ticketsRes, contactsRes] = await Promise.all([
                fetch(`${API_URL}/api/support/admin/tickets`, {
                    headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                    }
                }),
                fetch(`${API_URL}/api/support/admin/contacts`, {
                    headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                    }
                })
            ]);
            
            if (ticketsRes.ok) {
                const data = await ticketsRes.json();
                setSupportTickets(data);
            }
            if (contactsRes.ok) {
                const data = await contactsRes.json();
                setContactSubmissions(data.submissions || []);
            }
        } catch (error) {
            toast.error('Failed to fetch support data');
        } finally {
            setSupportLoading(false);
        }
    };

    const updateContactStatus = async (submissionId, status) => {
        try {
            const response = await fetch(`${API_URL}/api/support/admin/contacts/${submissionId}/status?status=${status}`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                }
            });
            
            if (response.ok) {
                toast.success(`Contact marked as ${status}`);
                fetchSupportTickets();
            } else {
                toast.error('Failed to update status');
            }
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    // Repair content handlers
    const fetchRepairContent = async () => {
        setRepairLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/content/repair-reconnection`);
            if (response.ok) {
                const data = await response.json();
                setRepairContent(data.sections ? data : {
                    title: 'Repair & Reconnection',
                    subtitle: 'Questions to guide important conversations and achieve repair',
                    intro: 'These prompting questions can help you navigate difficult conversations, stay grounded during emotional discussions, and work toward reconnection.',
                    sections: []
                });
            }
        } catch (error) {
            toast.error('Failed to fetch repair content');
        } finally {
            setRepairLoading(false);
        }
    };

    const saveRepairContent = async () => {
        try {
            const response = await fetch(`${API_URL}/api/admin/content/repair-reconnection`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('co_regulate_token')}`
                },
                body: JSON.stringify(repairContent)
            });
            
            if (response.ok) {
                toast.success('Repair content saved');
            } else {
                toast.error('Failed to save content');
            }
        } catch (error) {
            toast.error('Failed to save content');
        }
    };

    const addRepairSection = () => {
        const newSection = {
            id: `section_${Date.now()}`,
            title: 'New Section',
            description: '',
            questions: []
        };
        setRepairContent(prev => ({
            ...prev,
            sections: [...(prev?.sections || []), newSection]
        }));
    };

    // Load data on tab change
    useEffect(() => {
        if (activeTab === 'subscribers' && subscribersData.subscribers.length === 0) {
            fetchSubscribers();
        }
        if (activeTab === 'support' && supportTickets.tickets?.length === 0) {
            fetchSupportTickets();
        }
        if (activeTab === 'repair' && !repairContent) {
            fetchRepairContent();
        }
    }, [activeTab]);

    // Access denied
    if (isAdmin === false) {
        return (
            <Container className="min-h-screen flex items-center justify-center bg-[#FEF6EF]">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-[#FFBB98] mx-auto mb-4" />
                    <Heading as="h2" size="text-xl" className="mb-2">Access Denied</Heading>
                    <BodyText className="text-[#7D8E95] mb-4">You don't have permission to access this page.</BodyText>
                    <button 
                        onClick={() => navigate('/home')}
                        className="text-[#1D6B6B] underline"
                    >
                        Return Home
                    </button>
                </div>
            </Container>
        );
    }

    // Loading
    if (isAdmin === null || loading) {
        return (
            <Container className="min-h-screen flex items-center justify-center bg-[#FEF6EF]">
                <div className="text-center">
                    <div className="w-3 h-3 rounded-full bg-[#1D6B6B] animate-pulse mx-auto mb-4" />
                    <SmallText className="text-[#7D8E95]">Loading...</SmallText>
                </div>
            </Container>
        );
    }

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header */}
            <div className="sticky top-0 bg-[#FEF6EF] border-b border-[#FFBB98] z-40">
                <Container className="py-4">
                    <div className="flex items-center gap-4">
                        <IconButton onClick={() => navigate('/home')}>
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <div>
                            <Heading as="h1" size="text-xl">Content Management</Heading>
                            <SmallText className="text-[#7D8E95]">Manage tools, paths, and page content</SmallText>
                        </div>
                    </div>
                </Container>
            </div>
            
            {/* Tabs */}
            <div className="border-b border-[#FFBB98]/40 bg-[#FEF6EF]/80 backdrop-blur-sm">
                <Container>
                    <div className="flex gap-1 overflow-x-auto py-3 px-1">
                        {TAB_LIST.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all duration-300 ${
                                    activeTab === tab.id 
                                        ? 'bg-white/90 backdrop-blur-sm text-[#1D6B6B] shadow-soft' 
                                        : 'text-[#7D8E95] hover:text-[#2A8585] hover:bg-white/40'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </Container>
            </div>
            
            {/* Content */}
            <Container className="py-6">
                {activeTab === 'pages' && (
                    <PagesTab 
                        pageContents={pageContents} 
                        onUpdate={fetchData} 
                    />
                )}

                {activeTab === 'blog' && (
                    <BlogTab />
                )}

                {activeTab === 'testimonials' && (
                    <TestimonialsTab />
                )}
                
                {activeTab === 'tools' && (
                    <ToolsTab 
                        tools={tools}
                        filterType={filterType}
                        setFilterType={setFilterType}
                        onEdit={(tool) => { setEditingTool(tool); setIsNewTool(false); }}
                        onDelete={handleDeleteTool}
                        onPreview={setPreviewTool}
                        onCreateNew={() => { setEditingTool({}); setIsNewTool(true); }}
                    />
                )}
                
                {activeTab === 'meditations' && (
                    <MeditationsTab />
                )}
                
                {activeTab === 'voice' && (
                    <VoiceSettingsTab />
                )}
                
                {activeTab === 'paths' && (
                    <PathsTab 
                        paths={paths} 
                        tools={tools} 
                        onUpdate={fetchData} 
                    />
                )}
                
                {activeTab === 'assessment' && (
                    <AssessmentTab 
                        assessmentConfig={assessmentConfig}
                        editingAssessmentSection={editingAssessmentSection}
                        setEditingAssessmentSection={setEditingAssessmentSection}
                        onSave={saveAssessmentConfig}
                    />
                )}
                
                {activeTab === 'emotions' && (
                    <EmotionsTab 
                        emotions={emotions}
                        editingEmotion={editingEmotion}
                        setEditingEmotion={setEditingEmotion}
                        onSave={saveEmotion}
                    />
                )}
                
                {activeTab === 'education' && (
                    <EducationTab />
                )}
                
                {activeTab === 'scripts' && (
                    <ScriptsTab 
                        scripts={scripts}
                        editingScript={editingScript}
                        setEditingScript={setEditingScript}
                        onSave={saveScript}
                    />
                )}
                
                {activeTab === 'triggers' && (
                    <TriggersTab 
                        triggers={triggers}
                        editingTrigger={editingTrigger}
                        setEditingTrigger={setEditingTrigger}
                        onSave={saveTrigger}
                    />
                )}
                
                {activeTab === 'patterns' && (
                    <PatternsTab 
                        cyclePatterns={cyclePatterns}
                        editingPattern={editingPattern}
                        setEditingPattern={setEditingPattern}
                        onSave={savePattern}
                    />
                )}
                
                {activeTab === 'checkin' && (
                    <CheckinTab 
                        checkinQuestions={checkinQuestions}
                        safetyQuestions={safetyQuestions}
                        editingCheckin={editingCheckin}
                        setEditingCheckin={setEditingCheckin}
                        onSave={saveCheckin}
                    />
                )}
                
                {activeTab === 'subscribers' && (
                    <SubscribersTab 
                        subscribersData={subscribersData}
                        subscribersLoading={subscribersLoading}
                        onFetch={fetchSubscribers}
                    />
                )}
                
                {activeTab === 'support' && (
                    <SupportTab 
                        supportTickets={supportTickets}
                        contactSubmissions={contactSubmissions}
                        supportLoading={supportLoading}
                        onFetch={fetchSupportTickets}
                        onSelectTicket={setSelectedTicket}
                        onUpdateContactStatus={updateContactStatus}
                    />
                )}
                
                {activeTab === 'repair' && (
                    <RepairTab 
                        repairContent={repairContent}
                        repairLoading={repairLoading}
                        onContentChange={setRepairContent}
                        onSave={saveRepairContent}
                        onAddSection={addRepairSection}
                        editingRepairSection={editingRepairSection}
                        setEditingRepairSection={setEditingRepairSection}
                    />
                )}
                
                {activeTab === 'disclaimer' && (
                    <DisclaimerTab />
                )}
                
                {activeTab === 'pricing' && (
                    <SubscriptionTiersTab />
                )}
            </Container>
            
            {/* Tool Editor Modal */}
            {editingTool && (
                <ToolEditorModal
                    tool={editingTool}
                    isNew={isNewTool}
                    onSave={handleSaveTool}
                    onClose={() => { setEditingTool(null); setIsNewTool(false); }}
                />
            )}
        </div>
    );
};

export default ToolEditor;
